import { cloneDeep } from 'lodash';

import type { CustomerContext } from './customerContext/types';
import { createEmptyInsuranceOfferMemory } from './insuranceOffer/memory';
import type { InsuranceContactType, InsuranceOfferMemory } from './insuranceOffer/types';
import type { JarabotAttrValue, JarabotAttributes, JarabotUserIdentity } from './jarabotClient';

const ENABLED = process.env.TARGET_ENV !== 'prod';

const EVENT_LOG_LIMIT = 20;

export const UNDEFINED_MARKER = '<undefined>';

export type JarabotStoreAttrs = Record<string, JarabotAttrValue>;

export enum JarabotDevStoreEventKind {
	MESSAGE = 'message',
	TOGGLE = 'toggle',
}

export interface JarabotDevStoreEvent {
	kind: JarabotDevStoreEventKind;
	name: string;
	detail?: Record<string, unknown>;
	hidden?: boolean;
	at: string;
}

export interface JarabotDevStoreStatus {
	isReady: boolean;
	pendingCalls: number;
}

export interface JarabotDevStoreInsuranceSent {
	contactType: InsuranceContactType;
	offerIds: string[];
	at: string;
}

export interface JarabotDevStoreInsurance {
	sent: JarabotDevStoreInsuranceSent | null;
	memory: InsuranceOfferMemory;
}

export interface JarabotDevStoreState {
	status: JarabotDevStoreStatus;
	user: JarabotStoreAttrs | null;
	customAttributes: JarabotStoreAttrs;
	conversationCustomAttributes: JarabotStoreAttrs;
	context: CustomerContext;
	insurance: JarabotDevStoreInsurance;
	events: JarabotDevStoreEvent[];
}

const createEmptyInsurance = (): JarabotDevStoreInsurance => ({
	sent: null,
	memory: createEmptyInsuranceOfferMemory(),
});

const store: JarabotDevStoreState = {
	status: { isReady: false, pendingCalls: 0 },
	user: null,
	customAttributes: {},
	conversationCustomAttributes: {},
	context: {},
	insurance: createEmptyInsurance(),
	events: [],
};

const UNMASKED_IDENTITY_KEYS = ['country_code'];

const maskWord = (word: string): string =>
	`${word.slice(0, 1)}${'*'.repeat(Math.max(word.length - 1, 1))}`;

const maskName = (name: string): string =>
	name.split(/\s+/).filter(Boolean).map(maskWord).join(' ');

const maskEmail = (email: string): string => {
	const [local, domain] = email.split('@');
	return domain ? `${maskWord(local)}@${domain}` : maskWord(email);
};

const maskPhone = (phone: string): string =>
	`${'*'.repeat(Math.max(phone.length - 3, 0))}${phone.slice(-3)}`;

const IDENTITY_MASKS: Record<string, (value: string) => string> = {
	name: maskName,
	email: maskEmail,
	phone_number: maskPhone,
};

const maskIdentity = (attrs: JarabotUserIdentity): JarabotStoreAttrs =>
	Object.fromEntries(
		Object.entries(attrs).map(([key, value]): [string, JarabotAttrValue] => {
			if (value === undefined) return [key, UNDEFINED_MARKER];
			if (UNMASKED_IDENTITY_KEYS.includes(key)) return [key, value];
			return [key, (IDENTITY_MASKS[key] ?? maskWord)(value)];
		})
	);

const toStoreEntries = (attrs: JarabotAttributes): JarabotStoreAttrs =>
	Object.fromEntries(
		Object.entries(attrs).map(([key, value]): [string, JarabotAttrValue] => [
			key,
			value === undefined ? UNDEFINED_MARKER : value,
		])
	);

const readOnlySnapshot = (): JarabotDevStoreState => {
	const snapshot = cloneDeep(store);
	Object.freeze(snapshot.status);
	if (snapshot.user) Object.freeze(snapshot.user);
	Object.freeze(snapshot.customAttributes);
	Object.freeze(snapshot.conversationCustomAttributes);
	Object.freeze(snapshot.context);
	if (snapshot.insurance.sent) Object.freeze(snapshot.insurance.sent);
	Object.freeze(snapshot.insurance.memory);
	Object.freeze(snapshot.insurance);
	snapshot.events.forEach((event) => Object.freeze(event));
	Object.freeze(snapshot.events);
	return Object.freeze(snapshot);
};

function expose(): void {
	if (typeof window === 'undefined' || !window.skippay || window.skippay.jarabot) return;

	window.skippay.jarabot = {
		get store(): JarabotDevStoreState {
			return readOnlySnapshot();
		},
	};
}

function run(mutate: () => void): void {
	if (!ENABLED) return;
	try {
		mutate();
		expose();
	} catch {
		// silently swallow, no error needed
	}
}

function pushEvent(event: JarabotDevStoreEvent): void {
	store.events = [...store.events, event].slice(-EVENT_LOG_LIMIT);
}

export const jarabotDevStore = {
	store,

	trackStatus(status: JarabotDevStoreStatus): void {
		run(() => {
			store.status = { ...status };
		});
	},

	trackSetUser(identifier: string, attrs: JarabotUserIdentity): void {
		run(() => {
			store.user = { identifier, ...maskIdentity(attrs) };
		});
	},

	trackSetCustomAttributes(attrs: JarabotAttributes): void {
		run(() => {
			Object.assign(store.customAttributes, toStoreEntries(attrs));
		});
	},

	trackSetConversationCustomAttributes(attrs: JarabotAttributes): void {
		run(() => {
			Object.assign(store.conversationCustomAttributes, toStoreEntries(attrs));
		});
	},

	trackDeleteCustomAttribute(key: string): void {
		run(() => {
			delete store.customAttributes[key];
		});
	},

	trackDeleteConversationCustomAttribute(key: string): void {
		run(() => {
			delete store.conversationCustomAttributes[key];
		});
	},

	trackSendMessage(name: string, detail?: Record<string, unknown>, hidden?: boolean): void {
		run(() => {
			pushEvent({
				kind: JarabotDevStoreEventKind.MESSAGE,
				name,
				detail,
				hidden,
				at: new Date().toISOString(),
			});
		});
	},

	trackToggle(state?: 'open' | 'close'): void {
		run(() => {
			pushEvent({
				kind: JarabotDevStoreEventKind.TOGGLE,
				name: state ?? 'toggle',
				at: new Date().toISOString(),
			});
		});
	},

	trackContext(context: CustomerContext): void {
		run(() => {
			store.context = { ...context };
		});
	},

	trackInsuranceMemory(memory: InsuranceOfferMemory): void {
		run(() => {
			store.insurance = { ...store.insurance, memory };
		});
	},

	trackInsuranceSent(contactType: InsuranceContactType, offerIds: string[]): void {
		run(() => {
			store.insurance = {
				...store.insurance,
				sent: { contactType, offerIds, at: new Date().toISOString() },
			};
		});
	},

	trackReset(): void {
		run(() => {
			store.user = null;
			store.customAttributes = {};
			store.conversationCustomAttributes = {};
			store.context = {};
			store.insurance = createEmptyInsurance();
			store.events = [];
		});
	},
};
